import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLogo from "../../components/onboarding/OnboardingLogo";
import { onboardingShell, card, label, inputStyle, primaryBtn, errorText, helpText } from "../../components/onboarding/styles";
import { API_BASE } from "../../lib/api";
import { getCurrentCustomer, setCurrentCustomer } from "../../lib/customerSession";

const BUSINESS_TYPES = [
  "Private Limited Company", "LLP", "Partnership Firm", "Proprietorship", "One Person Company", "Other",
];

const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim",
  "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh",
];

const INDUSTRIES = [
  { label: "Manufacturing",         emoji: "🏭" },
  { label: "Trading",               emoji: "🚚" },
  { label: "Services",              emoji: "💼" },
  { label: "Construction",          emoji: "🏗️" },
  { label: "Retail",                emoji: "🛒" },
  { label: "Healthcare",            emoji: "💚" },
  { label: "Food and Beverage",     emoji: "🍴" },
  { label: "Other",                 emoji: "⚙️" },
];

const REVENUE_RANGES  = ["Under ₹50L", "₹50L to ₹1Cr", "₹1Cr to ₹5Cr", "₹5Cr to ₹20Cr", "Above ₹20Cr"];
const EMPLOYEE_RANGES = ["1 to 10", "11 to 50", "51 to 200", "201 to 500", "Above 500"];

const SOFTWARES = [
  { label: "Tally",            emoji: "📒", badge: "Most Popular" },
  { label: "Zoho CRM",         emoji: "🔵" },
  { label: "Zoho Books",       emoji: "📗" },
  { label: "Microsoft Excel",  emoji: "📊" },
  { label: "Google Sheets",    emoji: "🗂️" },
  { label: "Busy Accounting",  emoji: "🅱️" },
  { label: "Vyapar",           emoji: "🟣" },
  { label: "QuickBooks",       emoji: "🟢" },
  { label: "No Software",      emoji: "📝" },
  { label: "Other",            emoji: "➕" },
];

const REFERRAL = [
  "CA Recommendation", "Friend or Colleague", "LinkedIn", "Google Search", "WhatsApp", "Trade Show", "Other",
];

const initial = {
  company_name: "", business_type: "", industry: "", city: "", state: "",
  revenue_range: "", employee_range: "", software_used: [], gstin: "", referral_source: "",
};

export default function BusinessProfilePage() {
  const navigate = useNavigate();
  const [email] = useState(() => sessionStorage.getItem("onboarding_email") || "");
  const [formData, setFormData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState({});

  useEffect(() => {
    if (!email) navigate("/signup", { replace: true });
  }, [email, navigate]);

  const setField = (k, v) => {
    setFormData((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleSoftware = (name) => {
    setFormData((s) => {
      const has = s.software_used.includes(name);
      return { ...s, software_used: has ? s.software_used.filter((x) => x !== name) : [...s.software_used, name] };
    });
    if (errors.software_used) setErrors((e) => ({ ...e, software_used: undefined }));
  };

  const focus = (k, on) => setFocused((s) => ({ ...s, [k]: on }));

  const validateAll = () => {
    const e = {};
    if (!formData.company_name.trim())   e.company_name   = "Please enter your business name.";
    if (!formData.business_type)         e.business_type  = "Select a business type.";
    if (!formData.industry)               e.industry       = "Select your industry.";
    if (!formData.city.trim())            e.city           = "Please enter your city.";
    if (!formData.state)                  e.state          = "Select your state.";
    if (!formData.revenue_range)          e.revenue_range  = "Select a revenue range.";
    if (!formData.employee_range)         e.employee_range = "Select an employee range.";
    if (!formData.software_used.length)   e.software_used  = "Select at least one option.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setBanner("");
    const errs = validateAll();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Scroll to top so the user sees the first error.
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/business-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          ...formData,
          gstin: formData.gstin?.trim() ? formData.gstin.trim() : null,
          referral_source: formData.referral_source || null,
        }),
      });
      if (res.ok) {
        // Capture the customer id so the new multi-step wizard can target this row.
        let customerId = null;
        try {
          const body = await res.json();
          customerId = body?.customer?.id || null;
          if (customerId) sessionStorage.setItem("onboarding_customer_id", customerId);
        } catch (_e) { /* server may return {success:true} only — falls back to email lookup */ }

        // Google-authenticated customers already have a live session — this
        // is a self-serve activation, not the sales-assisted email funnel.
        // Skip the multi-step wizard, start the 15-day trial automatically,
        // and land straight on the dashboard (per the approved auth plan).
        const activeCustomer = (() => { try { return getCurrentCustomer(); } catch (_e) { return null; } })();
        if (activeCustomer?.auth_provider === "google" && customerId) {
          try {
            await fetch(`${API_BASE}/billing/trial/start`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ customer_id: customerId }),
            });
          } catch (_e) { /* best-effort — trial can still be started later from Billing */ }
          setCurrentCustomer({ ...activeCustomer, company_name: formData.company_name.trim(), status: "profile_complete" });
          navigate("/executive", { replace: true });
          return;
        }

        navigate("/setup");
        return;
      }
      setBanner("Something went wrong. Please try again.");
    } catch (_e) {
      setBanner("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---- small inline helpers ----
  const SelectChevron = useMemo(() => ({
    appearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: 32,
  }), []);

  const Pill = ({ active, onClick, children, testid }) => (
    <button type="button" data-testid={testid} onClick={onClick}
      style={{
        background: active ? "#059669" : "#ffffff",
        color: active ? "white" : "#374151",
        border: `1px solid ${active ? "#059669" : "#D1D5DB"}`,
        borderRadius: 20,
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
      }}>
      {children}
    </button>
  );

  const Card = ({ active, onClick, children, testid, padding = "16px 8px" }) => (
    <div
      data-testid={testid}
      onClick={onClick}
      style={{
        background: active ? "#DCFCE7" : "#ffffff",
        border: active ? "2px solid #059669" : "1px solid #E5E7EB",
        borderRadius: 8,
        padding,
        textAlign: "center",
        cursor: "pointer",
        position: "relative",
        transition: "all 0.15s ease",
      }}
      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.borderColor = "#059669"; e.currentTarget.style.background = "#F0FDF4"; } }}
      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.background = "#ffffff"; } }}
    >
      {active && (
        <span style={{ position: "absolute", top: 6, right: 6, width: 16, height: 16, background: "#059669", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      )}
      {children}
    </div>
  );

  return (
    <div data-testid="profile-page" style={onboardingShell}>
      <div style={{ marginBottom: 24 }}><OnboardingLogo /></div>

      {/* Progress bar */}
      <div style={{ width: 480, maxWidth: "100%", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ color: "#6B7280", fontSize: 12, fontWeight: 600 }}>Step 2 of 2</span>
          <span style={{ color: "#6B7280", fontSize: 12 }}>Business profile</span>
        </div>
        <div style={{ width: "100%", height: 4, background: "#E5E7EB", borderRadius: 2 }}>
          <div style={{ width: "50%", height: "100%", background: "#059669", borderRadius: 2, transition: "width 0.3s" }} />
        </div>
      </div>

      <div style={{ ...card, width: 600, maxWidth: "100%", padding: 40 }}>
        {banner && (
          <div data-testid="profile-banner" style={{ marginBottom: 12, padding: "10px 12px", background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", fontSize: 13, borderRadius: 8 }}>
            {banner}
          </div>
        )}
        <h2 style={{ color: "#1E3A5F", fontSize: 22, fontWeight: 700, margin: 0 }}>Tell us about your business</h2>
        <p style={{ color: "#6B7280", fontSize: 13, marginTop: 6, marginBottom: 24 }}>This helps us set up your C-ORE dashboard</p>
        <hr style={{ borderColor: "#F3F4F6", borderTop: "1px solid #F3F4F6", borderBottom: "none", borderLeft: "none", borderRight: "none", marginBottom: 24 }} />

        <form onSubmit={onSubmit} noValidate>
          {/* Business Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={label}>Business Name <span style={{ color: "#DC2626" }}>*</span></label>
            <input
              data-testid="profile-company"
              type="text"
              value={formData.company_name}
              onChange={(e) => setField("company_name", e.target.value)}
              onFocus={() => focus("company_name", true)} onBlur={() => focus("company_name", false)}
              placeholder="Your registered business name"
              style={inputStyle(focused.company_name)}
            />
            {errors.company_name && <p style={errorText}>{errors.company_name}</p>}
          </div>

          {/* Business Type */}
          <div style={{ marginBottom: 16 }}>
            <label style={label}>Business Type <span style={{ color: "#DC2626" }}>*</span></label>
            <select
              data-testid="profile-business-type"
              value={formData.business_type}
              onChange={(e) => setField("business_type", e.target.value)}
              onFocus={() => focus("business_type", true)} onBlur={() => focus("business_type", false)}
              style={{ ...inputStyle(focused.business_type), ...SelectChevron }}
            >
              <option value="" disabled>Select your business type</option>
              {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.business_type && <p style={errorText}>{errors.business_type}</p>}
          </div>

          {/* Industry */}
          <div style={{ marginBottom: 20 }}>
            <label style={label}>Your Industry <span style={{ color: "#DC2626" }}>*</span></label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 8 }}>
              {INDUSTRIES.map((it) => (
                <Card key={it.label} active={formData.industry === it.label}
                      onClick={() => setField("industry", it.label)}
                      testid={`profile-industry-${it.label.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>{it.emoji}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F" }}>{it.label}</div>
                </Card>
              ))}
            </div>
            {errors.industry && <p style={errorText}>{errors.industry}</p>}
          </div>

          {/* City + State */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={label}>City <span style={{ color: "#DC2626" }}>*</span></label>
              <input
                data-testid="profile-city"
                type="text"
                value={formData.city}
                onChange={(e) => setField("city", e.target.value)}
                onFocus={() => focus("city", true)} onBlur={() => focus("city", false)}
                placeholder="e.g. Pune"
                style={inputStyle(focused.city)}
              />
              {errors.city && <p style={errorText}>{errors.city}</p>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={label}>State <span style={{ color: "#DC2626" }}>*</span></label>
              <select
                data-testid="profile-state"
                value={formData.state}
                onChange={(e) => setField("state", e.target.value)}
                onFocus={() => focus("state", true)} onBlur={() => focus("state", false)}
                style={{ ...inputStyle(focused.state), ...SelectChevron }}
              >
                <option value="" disabled>Select state</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.state && <p style={errorText}>{errors.state}</p>}
            </div>
          </div>

          {/* Revenue */}
          <div style={{ marginBottom: 20 }}>
            <label style={label}>Annual Revenue <span style={{ color: "#DC2626" }}>*</span></label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {REVENUE_RANGES.map((r) => (
                <Pill key={r} active={formData.revenue_range === r}
                  onClick={() => setField("revenue_range", r)}
                  testid={`profile-revenue-${r.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>
                  {r}
                </Pill>
              ))}
            </div>
            {errors.revenue_range && <p style={errorText}>{errors.revenue_range}</p>}
          </div>

          {/* Employees */}
          <div style={{ marginBottom: 20 }}>
            <label style={label}>Number of Employees <span style={{ color: "#DC2626" }}>*</span></label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {EMPLOYEE_RANGES.map((r) => (
                <Pill key={r} active={formData.employee_range === r}
                  onClick={() => setField("employee_range", r)}
                  testid={`profile-employees-${r.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>
                  {r}
                </Pill>
              ))}
            </div>
            {errors.employee_range && <p style={errorText}>{errors.employee_range}</p>}
          </div>

          {/* Software */}
          <div style={{ marginBottom: 20 }}>
            <label style={label}>Software Currently Used <span style={{ color: "#DC2626" }}>*</span></label>
            <p style={{ ...helpText, marginTop: 0, marginBottom: 8 }}>Select all that apply</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
              {SOFTWARES.map((sw) => (
                <Card key={sw.label} active={formData.software_used.includes(sw.label)}
                      onClick={() => toggleSoftware(sw.label)}
                      testid={`profile-software-${sw.label.replace(/\s+/g, "-").toLowerCase()}`}
                      padding="10px 6px">
                  <div style={{ fontSize: 22 }}>{sw.emoji}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: "#1E3A5F", marginTop: 4 }}>{sw.label}</div>
                  {sw.badge && (
                    <div style={{ marginTop: 4 }}>
                      <span style={{ display: "inline-block", background: "#DCFCE7", color: "#059669", borderRadius: 8, padding: "1px 6px", fontSize: 9, fontWeight: 700 }}>{sw.badge}</span>
                    </div>
                  )}
                </Card>
              ))}
            </div>
            {errors.software_used && <p style={errorText}>{errors.software_used}</p>}
          </div>

          {/* GSTIN */}
          <div style={{ marginBottom: 16 }}>
            <label style={label}>GSTIN</label>
            <p style={{ ...helpText, marginTop: 0, marginBottom: 6 }}>Optional — helps connect your GST data</p>
            <input
              data-testid="profile-gstin"
              type="text"
              maxLength={15}
              value={formData.gstin}
              onChange={(e) => setField("gstin", (e.target.value || "").toUpperCase())}
              onFocus={() => focus("gstin", true)} onBlur={() => focus("gstin", false)}
              placeholder="27XXXXX1234X1ZX"
              style={inputStyle(focused.gstin)}
            />
          </div>

          {/* Referral */}
          <div style={{ marginBottom: 24 }}>
            <label style={label}>How did you hear about us</label>
            <p style={{ ...helpText, marginTop: 0, marginBottom: 6 }}>Optional</p>
            <select
              data-testid="profile-referral"
              value={formData.referral_source}
              onChange={(e) => setField("referral_source", e.target.value)}
              onFocus={() => focus("referral_source", true)} onBlur={() => focus("referral_source", false)}
              style={{ ...inputStyle(focused.referral_source), ...SelectChevron }}
            >
              <option value="" disabled>Select an option</option>
              {REFERRAL.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <button
            data-testid="profile-submit"
            type="submit"
            disabled={loading}
            style={{
              ...primaryBtn,
              background: loading ? "#10b981" : "#059669",
              opacity: loading ? 0.85 : 1,
              cursor: loading ? "default" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#047857"; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = "#059669"; }}
          >
            {loading && (
              <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.5)", borderTopColor: "white", borderRadius: "50%", display: "inline-block", animation: "core-spin 0.7s linear infinite" }} />
            )}
            {loading ? "Saving…" : "Complete Setup"}
          </button>
        </form>
      </div>

      <style>{`@keyframes core-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
